import { Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  HiOutlineDownload,
  HiOutlineDocumentText,
  HiOutlineViewList,
  HiOutlineFolder,
  HiOutlineFolderOpen,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineChevronDown,
  HiOutlineArrowLeft,
  HiOutlineCash,
  HiOutlineInformationCircle,
} from 'react-icons/hi';
import { FaFileDownload } from 'react-icons/fa';

import { PanelLayout } from '../../components/PanelLayout';
import { brlCurrencyFormat } from '../../helpers/brlCurrencyFormat';
import { getDepartmentName } from '../../helpers/getDepartment';
import { isPaymentRequired } from '../../helpers/isPaymentRequired';
import { getFilesByYearAndMonth, markFileAsViewed } from '../../operations/file';
import { AccountingFile } from '../../operations/file/types';
import { useAuth } from '../../hooks/useAuth';
import {
  Container,
  PageTitle,
  PageSubtitle,
  PageHeader,
  HeaderLeft,
  HeaderControls,
  SearchInput,
  ViewToggle,
  ViewToggleButton,
  MonthSwitcher,
  MonthButton,
  MonthLabel,
  FilesList,
  FileCard,
  FileCardHeader,
  FileInfo,
  FileName,
  FileType,
  FileAmount,
  FileMetaList,
  FileMeta,
  FileActions,
  StatusTag,
  DownloadButton,
  EmptyContainer,
  EmptyIcon,
  EmptyTitle,
  SkeletonCard,
  SkeletonHeader,
  SkeletonBox,
  FoldersGrid,
  FolderCard,
  FolderHeader,
  FolderIcon,
  FolderInfo,
  FolderName,
  FolderCount,
  FolderArrow,
  BackButton,
  Section,
  SectionHeader,
  SectionIcon,
  SectionInfo,
  SectionTitle,
  SectionSubtitle,
  SectionBadge,
  SectionToggle,
  SectionContent,
} from './styles';

type ViewMode = 'list' | 'folder';

export const AccountingFiles = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('');
  const [files, setFiles] = useState<AccountingFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [payableSectionOpen, setPayableSectionOpen] = useState(true);
  const [informationalSectionOpen, setInformationalSectionOpen] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getFilesByYearAndMonth(selectedDate.format('YYYY-MM'))
      .then((filesFromApi) => {
        if (mounted) {
          setFiles(
            filesFromApi.map((file) => ({
              ...file,
              department: getDepartmentName(file.department),
            })) as AccountingFile[]
          );
        }
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [selectedDate]);

  const handlePreviousMonth = () => {
    setSelectedDate(moment(selectedDate).subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setSelectedDate(moment(selectedDate).add(1, 'month'));
  };

  const handleDownloadClick = (file: AccountingFile) => {
    if (file.viewed_at) return;

    // registra a visualização no backend; o download segue pelo href do link
    markFileAsViewed(file.id).catch(() => {});

    const viewedAt = moment().format('YYYY-MM-DD HH:mm:ss');
    setFiles((prev) =>
      prev.map((f) =>
        f.id === file.id
          ? { ...f, viewed_at: viewedAt, viewed_by: user?.id }
          : f
      )
    );
  };

  const getFileStatus = (file: AccountingFile) => {
    const isPastDue = moment(file.due_at).isBefore(moment(), 'day');
    const isViewed = !!file.viewed_at;
    const requiresPayment = isPaymentRequired(file.requires_payment);

    if (isPastDue && !isViewed && requiresPayment) {
      return { label: 'VENCIDO', color: 'var(--danger)', icon: null };
    }
    if (isViewed) {
      return { label: 'BAIXADO', color: 'var(--success)', icon: <FaFileDownload /> };
    }
    return { label: 'NÃO BAIXADO', color: 'var(--info)', icon: null };
  };

  const filteredFiles = files
    .filter((file) =>
      file.description?.toLowerCase().includes(filter.toLowerCase())
    )
    .sort((a, b) => moment(b.competency_at).diff(moment(a.competency_at)));

  // Group files by department
  const groupedByDepartment = filteredFiles.reduce((acc, file) => {
    const dept = file.department || 'Outros';
    if (!acc[dept]) {
      acc[dept] = [];
    }
    acc[dept].push(file);
    return acc;
  }, {} as Record<string, AccountingFile[]>);

  const departments = Object.keys(groupedByDepartment).sort();

  const renderSkeleton = () => (
    <FilesList>
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonCard key={i}>
          <SkeletonHeader>
            <div>
              <SkeletonBox $width={200} $height={16} $marginBottom={4} />
              <SkeletonBox $width={100} $height={12} />
            </div>
            <SkeletonBox $width={80} $height={16} />
          </SkeletonHeader>
        </SkeletonCard>
      ))}
    </FilesList>
  );

  const renderEmpty = () => (
    <EmptyContainer>
      <EmptyIcon>
        <HiOutlineDocumentText />
      </EmptyIcon>
      <EmptyTitle>Nenhum documento encontrado</EmptyTitle>
    </EmptyContainer>
  );

  const renderFileCard = (file: AccountingFile, showDepartment = true) => {
    const status = getFileStatus(file);
    const isPastDue = moment(file.due_at).isBefore(moment(), 'day');
    const isViewed = !!file.viewed_at;
    const requiresPayment = isPaymentRequired(file.requires_payment);
    const isExpired = isPastDue && !isViewed && requiresPayment;
    const showPaymentInfo = requiresPayment;

    return (
      <FileCard key={file.id} $isExpired={isExpired} $isViewed={isViewed}>
        <FileCardHeader>
          <FileInfo>
            <FileName>
              {file.description}
              {showDepartment && <FileType>{file.department}</FileType>}
            </FileName>
          </FileInfo>
          <FileMetaList>
            <Tooltip title="Vencimento">
              <FileMeta>{moment(file.due_at).format('DD/MM/YYYY')}</FileMeta>
            </Tooltip>
            {showPaymentInfo && file.amount > 0 && (
              <FileAmount>{brlCurrencyFormat(file.amount)}</FileAmount>
            )}
          </FileMetaList>
          <FileActions>
            <StatusTag $color={status.color}>
              {status.icon}
              {status.label}
            </StatusTag>
            <DownloadButton
              href={file.file_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadClick(file);
              }}
            >
              <HiOutlineDownload />
            </DownloadButton>
          </FileActions>
        </FileCardHeader>
      </FileCard>
    );
  };

  const renderFolderView = () => {
    if (selectedDepartment) {
      const deptFiles = groupedByDepartment[selectedDepartment] || [];
      return (
        <>
          <BackButton onClick={() => setSelectedDepartment(null)}>
            <HiOutlineArrowLeft />
            Voltar
          </BackButton>
          <PageHeader style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 18 }}>{selectedDepartment}</h2>
            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
              {deptFiles.length} documento{deptFiles.length !== 1 ? 's' : ''}
            </span>
          </PageHeader>
          <FilesList>
            {deptFiles.map((file) => renderFileCard(file, false))}
          </FilesList>
        </>
      );
    }

    return (
      <FoldersGrid>
        {departments.map((dept) => (
          <FolderCard key={dept} onClick={() => setSelectedDepartment(dept)}>
            <FolderHeader>
              <FolderIcon>
                <HiOutlineFolderOpen />
              </FolderIcon>
              <FolderInfo>
                <FolderName>{dept}</FolderName>
                <FolderCount>
                  {groupedByDepartment[dept].length} documento{groupedByDepartment[dept].length !== 1 ? 's' : ''}
                </FolderCount>
              </FolderInfo>
              <FolderArrow>
                <HiOutlineChevronRight />
              </FolderArrow>
            </FolderHeader>
          </FolderCard>
        ))}
      </FoldersGrid>
    );
  };

  const payableFiles = filteredFiles.filter((file) => isPaymentRequired(file.requires_payment));
  const informationalFiles = filteredFiles.filter((file) => !isPaymentRequired(file.requires_payment));

  const renderListView = () => (
    <>
      <Section>
        <SectionHeader onClick={() => setPayableSectionOpen(!payableSectionOpen)} $clickable>
          <SectionIcon $color="var(--warning)">
            <HiOutlineCash />
          </SectionIcon>
          <SectionInfo>
            <SectionTitle>Guias para Pagamento</SectionTitle>
            <SectionSubtitle>Documentos que requerem pagamento</SectionSubtitle>
          </SectionInfo>
          <SectionBadge $color="var(--warning)">{payableFiles.length}</SectionBadge>
          <SectionToggle $isOpen={payableSectionOpen}>
            <HiOutlineChevronDown />
          </SectionToggle>
        </SectionHeader>
        <SectionContent $isOpen={payableSectionOpen}>
          {payableFiles.length > 0 ? (
            <FilesList style={{ paddingLeft: 48 }}>
              {payableFiles.map((file) => renderFileCard(file))}
            </FilesList>
          ) : (
            <div style={{ paddingLeft: 48, color: 'rgba(0,0,0,0.35)', fontSize: 13 }}>
              Nenhuma guia para pagamento em {selectedDate.format('MMMM [de] YYYY')}
            </div>
          )}
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader onClick={() => setInformationalSectionOpen(!informationalSectionOpen)} $clickable>
          <SectionIcon $color="var(--info)">
            <HiOutlineInformationCircle />
          </SectionIcon>
          <SectionInfo>
            <SectionTitle>Documentos Informativos</SectionTitle>
            <SectionSubtitle>Relatórios e documentos para consulta</SectionSubtitle>
          </SectionInfo>
          <SectionBadge $color="var(--info)">{informationalFiles.length}</SectionBadge>
          <SectionToggle $isOpen={informationalSectionOpen}>
            <HiOutlineChevronDown />
          </SectionToggle>
        </SectionHeader>
        <SectionContent $isOpen={informationalSectionOpen}>
          {informationalFiles.length > 0 ? (
            <FilesList style={{ paddingLeft: 48 }}>
              {informationalFiles.map((file) => renderFileCard(file))}
            </FilesList>
          ) : (
            <div style={{ paddingLeft: 48, color: 'rgba(0,0,0,0.35)', fontSize: 13 }}>
              Nenhum documento informativo em {selectedDate.format('MMMM [de] YYYY')}
            </div>
          )}
        </SectionContent>
      </Section>
    </>
  );

  return (
    <PanelLayout>
      <Container>
        <PageTitle>Documentos</PageTitle>
        <PageSubtitle>Gerencie e baixe os documentos e obrigações da sua empresa</PageSubtitle>
        <PageHeader>
          <HeaderLeft>
            <SearchInput
              placeholder="Pesquisar documento..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </HeaderLeft>
          <HeaderControls>
            <MonthSwitcher>
              <MonthButton onClick={handlePreviousMonth}>
                <HiOutlineChevronLeft />
              </MonthButton>
              <MonthLabel>
                {selectedDate.format('MMMM [de] YYYY')}
              </MonthLabel>
              <MonthButton onClick={handleNextMonth}>
                <HiOutlineChevronRight />
              </MonthButton>
            </MonthSwitcher>
            <ViewToggle>
              <ViewToggleButton
                $isActive={viewMode === 'list'}
                onClick={() => {
                  setViewMode('list');
                  setSelectedDepartment(null);
                }}
                title="Lista"
              >
                <HiOutlineViewList />
              </ViewToggleButton>
              <ViewToggleButton
                $isActive={viewMode === 'folder'}
                onClick={() => {
                  setViewMode('folder');
                  setSelectedDepartment(null);
                }}
                title="Pastas"
              >
                <HiOutlineFolder />
              </ViewToggleButton>
            </ViewToggle>
          </HeaderControls>
        </PageHeader>

        {isLoading ? (
          renderSkeleton()
        ) : filteredFiles.length === 0 ? (
          renderEmpty()
        ) : viewMode === 'folder' ? (
          renderFolderView()
        ) : (
          renderListView()
        )}
      </Container>
    </PanelLayout>
  );
};
